import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalSize } from '@max/shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '@max/shared/services/modal/mvd-modal.service';
import * as moment from 'moment';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import * as driverRecordUtils from '../../driverrecord/driver-record-utilities.class';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { ValidatedResponse } from '../../shared/models/validated-response.model';
import { IncidentCustomer } from '../models/incident-customer.model';
import { IncidentItemKey } from '../models/incident-item-key.model';
import { IncidentLandCategoryType } from '../models/incident-land-category-type.model';
import { IncidentVehicle } from '../models/incident-vehicle.model';
import { IncidentModel } from '../models/incident.model';
import { toDateOrDefault } from '../shared/data-helpers';
import { IncidentDialogContext } from '../shared/incident-dialog-context.model';

@Injectable()
export class IncidentService extends BaseApiService {
    public getCustomer(customerId: number): Observable<ValidatedResponse<IncidentCustomer>> {
        return this.get('Incident/Customer/' + customerId);
    }

    public getCustomerIncidents(itemKey: IncidentItemKey): Observable<any> {
        return this.post('Incident/ItemKey/', itemKey);
    }

    public getVehicle(vehicleId: number): Observable<ValidatedResponse<IncidentVehicle>> {
        return this.get('Incident/Vehicle/' + vehicleId);
    }

    public getLandCategoryTypes(): Observable<ValidatedResponse<IncidentLandCategoryType[]>> {
        return this.get('Incident/Land/Categories');
    }

    public getIncidentTypes() {
        return this.get('Incident/Type');
    }

    public getCDLStatusOnDate(incident: IncidentModel) {
        let customerNumberId = (incident && incident.customers && incident.customers[0] && incident.customers[0].id) || 0;

        return this.get(`incident/cdl/${customerNumberId}/${this.getFormattedDate(incident.occurrenceDate)}`).pipe(
            catchError((error) => this.handleError(error)));
    }

    public checkOosDl(incident: IncidentModel) {
        const clean: any = this.forTransport(JSON.stringify(incident));

        return this.post('incident/oos', clean, this.replacer).pipe(
            catchError((error) => this.handleError(error)));
    }

    public getIncidentTypeByGroup(incidentGroupTypeId: any) {
        return this.get('Incident/groupType/' + incidentGroupTypeId);
    }

    public getLatestEffectiveIncidentTypes() {
        return this.get('Incident/GroupType/IncidentTypes');
    }

    public getCorrectIncidentType(incident: IncidentModel) {
        let incidentTypeId = incident.incidentTypeId || 0;
        let incidentGroupTypeId = incident.incidentGroupTypeId || 0;

        return this.get(`incident/groupType/${incidentTypeId}/${incidentGroupTypeId}/${this.getFormattedDate(incident.occurrenceDate)}`).pipe(
            catchError((error) => this.handleError(error)));
    }

    public getMostCurrentIncidentType(incident: IncidentModel) {

        const clean: any = this.forTransport(JSON.stringify(incident));

        return this.post('incident/current/groupType', clean, this.replacer).pipe(
            catchError((error) => this.handleError(error)));
    }

    public getLastReevaluated(incidentNumberId: any) {

        return this.get('Incident/reevaluated/' + incidentNumberId);
    }

    public calculateCustomerPoints(customerNumberId: any) {

        return this.get('Incident/CalculatePoints/' + customerNumberId);
    }

    protected handleError(error: HttpErrorResponse) {
        if (error.error) {
            return observableThrowError(error.error);
        } else {
            return observableThrowError('Server error');
        }
    }

    protected replacer(key, value) {
        if (value === '') {
            return undefined;
        }

        return value;
    }

    private forTransport(json: string): any {
        const clone: any = JSON.parse(json);

        for (const propertyName in clone) {
            if (propertyName.startsWith('_')) {
                clone[propertyName.substr(1)] = clone[propertyName];
            }
        }

        return clone;
    }

    public getAllIncidentTypes() {
        return this.get('Incident/AllTypes');
    }

    public getAllIncidentGroupTypes() {
        return this.get('Incident/AllIncidentGroupTypes');
    }

    public getActiveIncidentGroupTypes() {
        return this.get('Incident/ActiveIncidentGroupTypes');
    }

    private getFormattedDate(occurrenceDate) {
        let dateDefault = toDateOrDefault(occurrenceDate);
        return moment(dateDefault).format('YYYY-MM-DD');
    }

    /**
     * Open incident modal.
     * @param incidentNumberId
     * @param incidentGroupTypeId
     * @param isReadonly
     */
    public openIncident = (incidentNumberId, incidentGroupTypeId, isReadonly: boolean = false, modalService: MvdModalService, callbackFn?: () => any): void => {
        this.getIncidentTypeByGroup(incidentGroupTypeId)
            .subscribe(selectedIncidentType => {
                const { componentType, data } = driverRecordUtils.generateIncidentDialogData(selectedIncidentType, incidentNumberId, isReadonly);
                this.showIncidentDialog(componentType, data, modalService, callbackFn);
            });
    };

    /**
     * Part of incident modal functionality.
     * @param content
     * @param context
     * @param modal
     * @param view
     * @param isReadonly
     */
    private showIncidentDialog = (content: any, data: IncidentDialogContext<IncidentModel>, modalService: MvdModalService, callbackFn?: () => any): void => {
        modalService.open(content, data, ModalSize.Medium).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => {
                if (!data.isReadOnly) {
                    if (!!callbackFn) {
                        callbackFn();
                    }
                }
            });
    };
}
